/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React from "react";
import { X, Key, AlertCircle, Phone, Lock } from "lucide-react";

const ResetPasswordModal = ({ isOpen, onClose, onConfirm, userData }) => {
  if (!isOpen) return null;

  const phoneNumber = userData?.phoneNumber || "";
  const willUsePhoneNumber = phoneNumber && phoneNumber.trim() !== "";
  const newPassword = willUsePhoneNumber ? phoneNumber : "11111111";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border shadow-xl shadow-black/10 animate-in fade-in slide-in-from-bottom-4 duration-300">
          {/* Header */}
          <div className="relative p-6 pb-0">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
                  <Key size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-text dark:text-dark-text">
                    Reset Password
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-dark-muted">
                    Admin action required
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-dark-muted transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 pt-4">
            {/* Alert */}
            <div className="mb-6 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex gap-3">
                <AlertCircle
                  className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                  size={18}
                />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">
                    This action cannot be undone
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400/80">
                    The user will need to use the new password for their next
                    login
                  </p>
                </div>
              </div>
            </div>

            {/* User Info */}
            <div className="mb-6 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-center gap-3 mb-4">
                {userData?.image ? (
                  <img
                    src={userData.image}
                    alt={userData.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                    <span className="text-primary font-bold text-sm">
                      {userData?.name?.charAt(0) || "U"}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-medium text-text dark:text-dark-text">
                    {userData?.name || "User"}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-dark-muted">
                    {userData?.email || "No email"}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Phone Number:
                  </span>
                  <span className="font-medium text-text dark:text-dark-text">
                    {phoneNumber || "Not provided"}
                  </span>
                </div>
              </div>
            </div>

            {/* Password Info */}
            <div className="mb-6 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-3 mb-3">
                {willUsePhoneNumber ? (
                  <Phone
                    className="text-emerald-600 dark:text-emerald-400"
                    size={18}
                  />
                ) : (
                  <Lock
                    className="text-emerald-600 dark:text-emerald-400"
                    size={18}
                  />
                )}
                <div>
                  <p className="font-medium text-emerald-800 dark:text-emerald-300">
                    New Password Will Be:
                  </p>
                </div>
              </div>

              <div className="text-center">
                <div className="inline-block px-4 py-3 bg-white dark:bg-gray-800 rounded-xl border-2 border-emerald-200 dark:border-emerald-700">
                  <p className="text-xl font-mono font-bold text-emerald-700 dark:text-emerald-300 tracking-wider">
                    {newPassword}
                  </p>
                </div>
                <p className="text-xs text-emerald-700 dark:text-emerald-400/80 mt-3">
                  {willUsePhoneNumber
                    ? "Using user's phone number as password"
                    : "Using default password (11111111)"}
                </p>
              </div>
            </div>

            {/* Instructions */}
            <div className="mb-8">
              <p className="text-sm text-gray-600 dark:text-dark-muted mb-3">
                After resetting:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-primary/10 dark:bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">1</span>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">
                    User must login with the new password above
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-primary/10 dark:bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold">2</span>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">
                    User can change password anytime from profile settings
                  </span>
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-5 py-3 rounded-xl border border-gray-300 dark:border-dark-border text-text dark:text-dark-text font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className="flex-1 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <Key size={18} />
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
